import DriverPolicyUpdate from '../models/Driver';
import VehiclePolicyUpdate from '../models/Vehicle';
import { EnrollmentPolicyUpdate } from '../models/EnrollmentPolicyUpdate';
import UCMStates from '../models/UCMState';
import { UpdatePolicyInfoRequest } from '../models/PolicyInfo';
import { EnrollPolicyRequest } from '../models/EnrollPolicyRequest';
import { AddPolicyInfoRequest } from '../models/AddPolicyInfoRequest';
import { ReplaceMobileNumbersRequest } from '../models/ReplaceMobileNumbersRequest';
import { AuthProvider } from './authApi';
import BatchJobSettings from '../models/BatchJobSettings';
import { TagReplacementRequest } from '../models/TagReplacementRequest';
import { ReplaceObdDeviceRequest } from '../models/ReplaceObdDeviceRequest';
import { ReverseGeocodeRequest } from '../models/ReverseGeocodeRequest';
import { getCookie } from 'typescript-cookie';
import { logout } from './Identity';
import { DashcamPartsReplacementRequest } from '../models/DashcamPartsReplacementRequest';
import GustoFlagStatus, { GustoFlag } from '../models/GustoFlagStatus';
import { SetDriveEasyProgramStatusRequest } from '../models/AllDriveEasyProgram';

const getEssidCookie = (): boolean | null => {
    const value = localStorage.getItem("returnUrl") ?? "";
    const clientId = localStorage.getItem("clientId") ?? "";
    if (value === "" && clientId === "") {
        return false;
    }
    else if ((value !== null && value !== "" && value.includes("clientId")) || clientId !== "") {
        return true;
    }
    else if (value !== null && value !== "") {
        return true;
    }
    else {
        return false;
    }
};
const essidCookie = getEssidCookie();

export default class TelematicsApi {

    static enrollPolicy(enrollPolicyRequest: EnrollPolicyRequest) {
        if (enrollPolicyRequest.vehicles != undefined) {
            var i = enrollPolicyRequest.vehicles?.length;
            while (i--) {
                if (enrollPolicyRequest.vehicles[i].year.match(/\D/g) != null) {
                    enrollPolicyRequest.vehicles.splice(i, 1);
                }
            }
        }

        const requestOptions = this.createPostObject(enrollPolicyRequest);
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/telematics/EnrollPolicy', requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/EnrollPolicy?policyNumber=' + enrollPolicyRequest.policyNumber, requestOptions);
        }
    }

    static addPolicyInfoFromCproa(addPolicyInfoRequest: AddPolicyInfoRequest): Promise<any> {
        const requestOptions = this.createPostObject(addPolicyInfoRequest);
        return TelematicsFetch('/ui/api/telematics/AddPolicyInfoFromCproa?policyNumber=' + addPolicyInfoRequest.policyNumber, requestOptions);
    }

    static getPolicyByPolicyNumber(policyNumber: string, searchOption: string): Promise<any> {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/telematics/search?policyNumber=' + policyNumber + '&searchOption=' + searchOption, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/search?policyNumber=' + policyNumber + '&searchOption=' + searchOption, requestOptions);
        }
    }

    static getPolicyWithoutCproaByPolicyNumber(policyNumber: string, searchOption: string): Promise<any> {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/telematics/searchWithoutCproa?policyNumber=' + policyNumber + '&searchOption=' + searchOption, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/searchWithoutCproa?policyNumber=' + policyNumber + '&searchOption=' + searchOption, requestOptions);
        }
    }

    static upsertDriver(policy: DriverPolicyUpdate) {
        const requestOptions = this.createPostObject(policy);
        return TelematicsFetch('/ui/api/telematics/addUpdateDriver', requestOptions);
    }

    static upsertVehicle(policy: VehiclePolicyUpdate) {
        const requestOptions = this.createPostObject(policy);
        return TelematicsFetch('/ui/api/telematics/addUpdateVehicle', requestOptions);
    }

    static removeVehicle(policy: VehiclePolicyUpdate) {
        const requestOptions = this.createPostObject(policy);
        return TelematicsFetch('/ui/api/telematics/removeVehicle', requestOptions);
    }

    static getBatchJobSettings() {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/usercontrol/getBatchJobSettings', requestOptions);
    }

    static putBatchJobSettings(batchJobs: BatchJobSettings) {
        const requestOptions = this.createPutObject({ batchJobs });
        return TelematicsFetch('/api/usercontrol/putBatchJobSettings', requestOptions);
    }

    static getGustoFlagStatus(policyNumber?: string, onlyGustoUrls?: boolean) {
        const requestOptions = this.createGetObject();
        const urlParam = onlyGustoUrls ? '?onlyGustoUrls=true' : '';
        if (!essidCookie) {
            return TelematicsFetch('/api/usercontrol/getGustoFlagStatus' + urlParam, requestOptions);
        }
        else {
            let url = '/ui/api/Dashboard/getGustoFlagStatus';
            const params: string[] = [];

            if (policyNumber) {
                params.push('policyNumber=' + policyNumber);
            }
            if (onlyGustoUrls) {
                params.push('onlyGustoUrls=true');
            }

            if (params.length > 0) {
                url += '?' + params.join('&');
            }

            return DashboardFetch(url, requestOptions);
        }

    }

    static putGustoFlagStatus(gustoFlags: GustoFlagStatus) {
        const requestOptions = this.createPutObject({gustoFlags});
        return TelematicsFetch('/api/usercontrol/putGustoFlagStatus', requestOptions);
    }

    static addNewGustoFlagStatus(gustoFlags: GustoFlagStatus) {
        const requestOptions = this.createPutObject({gustoFlags});
        return TelematicsFetch('/api/usercontrol/addNewGustoFlagStatus', requestOptions);
    }

    static getStatesVolInVol() {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/stateucm', requestOptions);
    }

    static async getAllDriveEasyProgramEligibility() {
        const requestOptions = this.createGetObject();
        return await TelematicsFetch('/ui/api/Telematics/GetAllDriveEasyProgramEligibility', requestOptions);
    }

    static async updateAllDriveEasyProgramStatus(setDriveEasyProgramStatusRequest: SetDriveEasyProgramStatusRequest) {
        const requestOptions = this.createPostObject(setDriveEasyProgramStatusRequest);
        return await TelematicsFetchResponseWithoutJson('/ui/api/Telematics/SetDriveEasyProgramStatus', requestOptions);
    }

    static getStateHistory(state: string) {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/stateucm/getStateHistory?state=' + state, requestOptions);
    }

    static upsertPolicyEnrollment(policy: EnrollmentPolicyUpdate) {
        const requestOptions = this.createPostObject(policy);
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/telematics/UnenrollPolicy', requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/UnenrollPolicy?policyNumber=' + policy.policyNumber, requestOptions);
        }
    }

    static updateVoluntaryStates(states: UCMStates) {
        const requestOptions = this.createPutObject({ states });
        return TelematicsFetch('/api/stateucm', requestOptions);
    }

    static updatePolicyInformation(policy: UpdatePolicyInfoRequest): Promise<any> {
        const requestOptions = this.createPostObject(policy);
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/telematics/updatePolicyInformation?policyNumber=' + policy.policyNumber, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/updatePolicyInformation?policyNumber=' + policy.policyNumber, requestOptions);
        }
    }

    static startDriverEnrollmentIncompleteJob() {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/jobs/driverEnrollmentIncomplete', requestOptions);
    }

    static startVehicleDataNotReceivedJob() {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/jobs/vehicleDataNotReceived', requestOptions);
    }

    static startDriverHeartbeatNotReceivedJob() {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/jobs/driverHeartbeatNotReceived', requestOptions);
    }

    static startTagOnlyTripsJob() {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/jobs/tagOnlyTrips', requestOptions);
    }

    static startVehicleEnrollmentIncomplete() {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/jobs/vehicleEnrollmentIncomplete', requestOptions);
    }

    static startVendorDriverSummaryJob() {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/jobs/vendorDriverSummary', requestOptions);
    }

    static startVendorTagTripSummaryJob() {
        const requestOptions = this.createGetObject();
        return TelematicsFetch('/api/jobs/vendorTagTripSummary', requestOptions);
    }

    static deleteCmtData(removalDate: string) {
        const requestOptions = this.createPostObject(removalDate);
        return TelematicsFetch('/api/jobs/DeleteOldTelematicsData?inputDate=' + removalDate, requestOptions);
    }

    static getDashcamTelematicsData(policyNumber: string) {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
            return DashcamFetch('/ui/api/Telematics/GetDashcamData?policyNumber=' + policyNumber, requestOptions);
        }
        else {
            return DashcamDashboardFetch('/ui/api/Dashboard/GetDashcamData?policyNumber=' + policyNumber, requestOptions);
        }
    }

    static requestDashcamShippingData(policyNumber: string) {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
            return DashcamFetch('/ui/api/Telematics/RequestDashcamShippingData?policyNumber=' + policyNumber, requestOptions);
        }
        else {
            return DashcamDashboardFetch('/ui/api/Dashboard/RequestDashcamShippingData?policyNumber=' + policyNumber, requestOptions);
        }
    }

    static getVendorInformation(policyNumber: string) {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/Telematics/getVendorInformation?policyNumber=' + policyNumber, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/getVendorInformation?policyNumber=' + policyNumber, requestOptions);
        }
    }

    static getVehicleTagData(policyNumber: string) {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/Telematics/GetVehicleTagData?policyNumber=' + policyNumber, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/GetVehicleTagData?policyNumber=' + policyNumber, requestOptions);
        }
    }

    static replaceMobileNumbers(replaceMobileNumbersRequest: ReplaceMobileNumbersRequest) {
        const requestOptions = this.createPostObject(replaceMobileNumbersRequest);
        return TelematicsFetch('/ui/api/Telematics/ReplaceMobileNumbers', requestOptions);
    }

    static tagReplacement(tagReplacementRequest : TagReplacementRequest) {
        const requestOptions = this.createPostObject(tagReplacementRequest);
        return TelematicsFetchResponseWithoutJson('/ui/api/Telematics/TagReplacement', requestOptions);
    }

    static getClips(serialNumber: string, policyNumber?: string) {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/Telematics/GetClips?serialNumber=' + serialNumber, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/GetClips?serialNumber=' + serialNumber + "&policyNumber=" + policyNumber, requestOptions);
        }
    }

    static getLastKnownLocationByPolicyNumber(policyNumber: string) {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
        return TelematicsFetch('/ui/api/Telematics/GetLastKnownLocationByPolicyNumber?policyNumber=' + policyNumber, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/GetLastKnownLocationByPolicyNumber?policyNumber=' + policyNumber, requestOptions);
        }
    }

    static getLastKnownLocationByVin(vin: string) {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
        return TelematicsFetch('/ui/api/Telematics/GetLastKnownLocationByVin?vin=' + vin, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/GetLastKnownLocationByVin?vin=' + vin, requestOptions);
        }
    }

    static getObdShipmentStatus(policyNumber: string) {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
        return TelematicsFetch('/ui/api/Telematics/GetObdShipmentStatus?policyNumber=' + policyNumber, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/GetObdShipmentStatus?policyNumber=' + policyNumber, requestOptions);
        }
    }

    static replaceObdDevice(replaceObdDeviceRequest: ReplaceObdDeviceRequest) {
        const requestOptions = this.createPutObject(replaceObdDeviceRequest);
        if (!essidCookie) {
        return TelematicsFetch('/ui/api/Telematics/ReplaceObdDevice', requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/ReplaceObdDevice', requestOptions);
        }
    }

    static reverseGeocode(reverseGeocodeRequest: ReverseGeocodeRequest, policyNumber?: string) {
        const requestOptions = this.createPostObject(reverseGeocodeRequest);
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/Telematics/ReverseGeocode', requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/ReverseGeocode?policyNumber=' + policyNumber, requestOptions);
        }
    }

    static getDriveEasyProgramEligibilityByPolicyNumber(policyNumber: string) {
        const requestOptions = this.createGetObject();
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/Telematics/GetDriveEasyProgramEligibilityByPolicyNumber?policyNumber=' + policyNumber, requestOptions);
        }
        else {
            return DashboardFetch('/ui/api/Dashboard/GetDriveEasyProgramEligibilityByPolicyNumber?policyNumber=' + policyNumber, requestOptions);
        }
    }

    static dashcamPartsReplacement(dashcamPartsReplacementRequest : DashcamPartsReplacementRequest) {
        const requestOptions = this.createPostObject(dashcamPartsReplacementRequest);
        if (!essidCookie) {
            return TelematicsFetchResponseWithoutJson('/ui/api/Telematics/DashcamPartsReplacement', requestOptions);
        }
        else {
            return DashboardFetchResponseWithoutJson('/ui/api/Dashboard/DashcamPartsReplacement?policyNumber=' + dashcamPartsReplacementRequest.policyNumber, requestOptions);
        }

    }

    static retriggerReturnReminderEmails(policyNumber : string) {
        const requestOptions = this.createPostObject(policyNumber);
        if (!essidCookie) {
            return TelematicsFetch('/ui/api/Telematics/RetriggerReturnReminderEmails', requestOptions);
        }
        else {
            return DashcamDashboardFetch('/ui/api/Dashboard/RetriggerReturnReminderEmails?policyNumber=' + policyNumber, requestOptions);
        }
    }

    static createGetObject<T>(): any {
        return {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + AuthProvider.getToken()
            },
        };
    }

    static createPostObject<T>(data: T): any {
        return {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + AuthProvider.getToken()
            },
            body: JSON.stringify(data),
        };
    }

    static createEmptyPostObject(): any {
        return {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + AuthProvider.getToken()
            },
        };
    }

    static createPutObject<T>(data: T): any {
        return {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + AuthProvider.getToken()
            },
            body: JSON.stringify(data)
        }
    }
}

const TelematicsFetch = (url: string, requestOptions: any) => {
    return fetch(url, requestOptions)
        .then(res => {
            if(res.status == 200){
                return res.json();
            }
            else if(res.status == 201 || res.status == 206){
                return res;
            }
            else {
                return Promise.reject({
                    status: res.status,
                    statusText: res.statusText,
                    url,
                    requestOptions
                });
            }
        });
}

const TelematicsFetchResponseWithoutJson = (url: string, requestOptions: any) => {
    return fetch(url, requestOptions)
        .then(res => {
            if(res.status == 200){
                return res;
            }
            else {
                return Promise.reject({
                    status: res.status,
                    statusText: res.statusText,
                    url,
                    requestOptions
                });
            }
        });
}

const DashboardFetchResponseWithoutJson = (url: string, requestOptions: any) => {
    return fetch(url, requestOptions)
        .then(res => {
            if(res.status == 200){
                return res;
            }
            else if (res.status == 401) {
                AuthProvider.logoutCustomerOrCounselor();
            }
            else {
                return Promise.reject({
                    status: res.status,
                    statusText: res.statusText,
                    url,
                    requestOptions
                });
            }
        });
}

const DashcamFetch = (url: string, requestOptions: any) => {
    return fetch(url, requestOptions)
        .then(res => {
            if(res.status == 200){
                return res.json();
            }
            else if(res.status == 204){
                return res;
            }
            else {
                return Promise.reject({
                    status: res.status,
                    statusText: res.statusText,
                    url,
                    requestOptions
                });
            }
        });
}

const TelematicsFileFetch = (url: string, requestOptions: any) => {
    return fetch(url, requestOptions)
        .then((res: Response): Promise<any> => {
            if (res.status == 200) {
                return res.blob();
            }
            else {
                return Promise.reject({
                    status: res.status,
                    statusText: res.statusText,
                    url,
                    requestOptions
                });
            }
        });
}

const DashboardFetch = (url: string, requestOptions: any) => {
    return fetch(url, requestOptions)
        .then(res => {
            if (res.status == 200) {
                return res.json();
            }
            else if (res.status == 201 || res.status == 206) {
                return res;
            }
            else if (res.status == 401) {
                AuthProvider.logoutCustomerOrCounselor();
            }
            else {
                return Promise.reject({
                    status: res.status,
                    statusText: res.statusText,
                    url,
                    requestOptions
                });
            }
        });
}

const DashcamDashboardFetch = (url: string, requestOptions: any) => {
    return fetch(url, requestOptions)
        .then(res => {
            if(res.status == 200){
                return res.json();
            }
            else if(res.status == 204){
                return res;
            }
            else if (res.status == 401) {
                AuthProvider.logoutCustomerOrCounselor();
            }
            else {
                return Promise.reject({
                    status: res.status,
                    statusText: res.statusText,
                    url,
                    requestOptions
                });
            }
        });
}
